/**
 * 礼包限购与 reset_at 倒计时（packages_info config_list 字段）
 *
 * @typedef {Object} PackageListItem
 * @property {number} [purchase_limit_type] 0 非限购，1 终身限购，2 周期性限购
 * @property {number} [purchase_limit_cycle] 0 非周期，1 自然日，2 自然周，3 自然月
 * @property {number} [purchase_count] 周期/终身可购次数上限
 * @property {number} [remain_count] 剩余可购次数
 * @property {number|string|null} [reset_at] 下次重置 unix 时间戳（秒）；周期限购次数用尽时有值
 */

/** 从 list 项或 item.package 取限购字段 */
function pickLimitField(item, key) {
  if (!item) return undefined
  const v = item[key]
  if (v != null && v !== '') return v
  const pkg = item.package
  if (pkg && typeof pkg === 'object') {
    const pv = pkg[key]
    if (pv != null && pv !== '') return pv
  }
  return undefined
}

function getPurchaseLimitType(item) {
  const t = Number(pickLimitField(item, 'purchase_limit_type'))
  return Number.isNaN(t) ? 0 : t
}

function getRemainCount(item) {
  const v = pickLimitField(item, 'remain_count')
  if (v == null || `${v}`.length === 0) return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

function getPurchaseCountLimit(item) {
  const v = pickLimitField(item, 'purchase_count')
  if (v == null || `${v}`.length === 0) return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

/**
 * 支付埋点 call_payment 限购维度
 * - multiple_num：1 非限购，2 终身限购，3 周期限购
 * - apply_num：已购次数 purchase_count - remain_count
 * - drop_num：剩余可购 remain_count
 */
export function getCallPaymentLimitTrackingProps(raw) {
  if (!raw || typeof raw !== 'object') {
    return { multiple_num: 1, apply_num: 0, drop_num: 0 }
  }

  const t = getPurchaseLimitType(raw)
  let multiple_num = 1
  if (t === 1) multiple_num = 2
  else if (t === 2) multiple_num = 3

  const remain = getRemainCount(raw)
  const purchase = getPurchaseCountLimit(raw)
  let apply_num = 0
  let drop_num = 0

  if (multiple_num !== 1 && remain != null && purchase != null) {
    drop_num = Math.max(0, Math.floor(remain))
    apply_num = Math.max(0, Math.floor(purchase) - drop_num)
  }

  return { multiple_num, apply_num, drop_num }
}

/** 限购次数已满（remain_count === 0） */
export function isPackagePurchaseLimitReached(item) {
  const t = getPurchaseLimitType(item)
  if (t !== 1 && t !== 2) return false
  const remain = getRemainCount(item)
  return remain != null && remain <= 0
}

/** 终身限购（purchase_limit_type=1），供限购条配色区分 */
export function isLifetimePackagePurchaseLimit(item) {
  return getPurchaseLimitType(item) == 1
}

/** 自然月周期限购（purchase_limit_type=2 且 purchase_limit_cycle=3），供限购条配色区分 */
export function isMonthlyPackagePurchaseLimit(item) {
  if (getPurchaseLimitType(item) !== 2) return false
  const c = Number(pickLimitField(item, 'purchase_limit_cycle'))
  return !Number.isNaN(c) && c === 3
}

/**
 * reset_at 转毫秒；后端为 unix 秒级时间戳
 * @returns {number|null}
 */
export function getPackageResetAtMs(item) {
  const v = pickLimitField(item, 'reset_at')
  if (v == null || v === '') return null
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return null
  return n < 1e12 ? n * 1000 : n
}

/**
 * 周期性限购次数用尽且存在 reset_at 时展示倒计时
 */
export function shouldShowPackageResetCountdown(item) {
  if (getPurchaseLimitType(item) !== 2) return false
  if (!isPackagePurchaseLimitReached(item)) return false
  const resetMs = getPackageResetAtMs(item)
  if (resetMs == null) return false
  return resetMs > Date.now()
}

export function shouldShowResetCountdown(item) {
  if (getPurchaseLimitType(item) !== 2) return false
  const resetMs = getPackageResetAtMs(item)
  if (resetMs == null) return false
  return true
}

/**
 * 距下一周期 reset_at 的倒计时文案（商品卡按钮 / Rewards Detail 弹窗共用）
 * i18n：countdown.days / countdown.hours / countdown.minutes，{0}{1} 为数值占位
 */
export function formatPackageResetCountdownText(vm, item, nowMs = Date.now()) {
  if (!vm?.$t) return ''
  const resetMs = getPackageResetAtMs(item)
  if (!resetMs) return ''
  const left = resetMs - nowMs
  if (left <= 0) return ''

  const dayMs = 86400000
  const hourMs = 3600000
  const minMs = 60000

  if (left >= dayMs) {
    const day = Math.floor(left / dayMs)
    const hour = Math.floor((left % dayMs) / hourMs)
    return vm.$t('countdown.days', [day, hour])
  }

  if (left >= hourMs) {
    const hour = Math.floor(left / hourMs)
    const minute = Math.floor((left % hourMs) / minMs)
    return vm.$t('countdown.hours', [hour, minute])
  }

  const minute = Math.floor(left / minMs)
  const second = Math.floor((left % minMs) / 1000)
  return vm.$t('countdown.minutes', [minute, second])
}

/**
 * 距下一周期 reset_at 的倒计时文案（商品卡按钮 / Rewards Detail 弹窗共用）
 * i18n：countdown.days / countdown.hours / countdown.minutes，{0}{1} 为数值占位
 */
export function formatPackageResetCountdownBtnText(vm, item, nowMs = Date.now()) {
  if (!vm?.$t) return ''
  const resetMs = getPackageResetAtMs(item)
  if (!resetMs) return ''
  const left = resetMs - nowMs
  if (left <= 0) return ''

  const dayMs = 86400000
  const hourMs = 3600000
  const minMs = 60000

  if (left >= dayMs) {
    const day = Math.floor(left / dayMs)
    const hour = Math.floor((left % dayMs) / hourMs)
    return vm.$t('countdown.dayHour', [day, hour])
  }

  if (left >= hourMs) {
    const hour = Math.floor(left / hourMs)
    const minute = Math.floor((left % hourMs) / minMs)
    return vm.$t('countdown.hourMinute', [hour, minute])
  }

  const minute = Math.floor(left / minMs)
  const second = Math.floor((left % minMs) / 1000)
  return vm.$t('countdown.minuteSecond', [minute, second])
}

/**
 * 限购条 i18n 元数据，供组件拆分 {0}/{1} 占位符（如月限购可购数标红）
 * @returns {{ key: string, remain: number, purchase: number } | null}
 *   key: goods.limit_*；remain: 可购数 remain_count；purchase: 上限 purchase_count
 */
export function getPackagePurchaseLimitMeta(item) {
  if (!item) return null

  const t = getPurchaseLimitType(item)
  if (t === 0) return null

  const remain = getRemainCount(item)
  const purchase = getPurchaseCountLimit(item)
  if (remain == null || purchase == null) return null

  let key = ''
  if (t === 1) key = 'goods.limit_permanent'
  else if (t === 2) {
    const c = Number(pickLimitField(item, 'purchase_limit_cycle'))
    if (c === 1) key = 'goods.limit_day'
    else if (c === 2) key = 'goods.limit_week'
    else if (c === 3) key = 'goods.limit_month'
  }

  if (!key) return null
  return { key, remain, purchase }
}

/**
 * 限购条完整文案
 * @returns {string} goods.limit_*，{0}=remain_count {1}=purchase_count
 */
export function formatPackagePurchaseLimitDisplay(vm, item) {
  if (!vm?.$t) return ''
  const meta = getPackagePurchaseLimitMeta(item)
  if (!meta) return ''
  return vm.$t(meta.key, [meta.remain, meta.purchase])
}
